// components/ActionButtons.js
import React, { useState } from 'react';
import { Button, Menu, MenuItem } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import DensitySmallIcon from '@mui/icons-material/DensitySmall';
import { handleAddPosition, handleDeleteFromOldGrid } from "../util/GridOperations";
import { placeKiteOrder } from "../../../buysellpopup/utils/BuySellUtil";
import { useSharingContext } from "../../../contexts/SharingContext";
import { handleShowDetails } from "@marketwatch/gridcomponents/guiinstrumentrow/MoreDetails/MoreDetailsOperations";
import { useAnnouncementContext } from "../../../contexts/ExchangeAnnContext";
import { useInstrumentRowContext } from "../../../contexts/InstrumentRowContext";
import {
    handleShowAnnouncementsMW
} from "@dashboard/grid-components/announcements/AnnouncementUtil"; // adjust path
import BuySellPopup from '@dashboard/buysellpopup/BuySellPopup';
import { tickStorage } from "@dashboard/contexts/tickStore";
import PriceVolumeAlertPopup from "@dashboard/pricevolumealerts/PriceVolumeAlertPopup";

const ActionButtons = ({ onShowChart }) => {
    const {
        currentlyHeldPositions,
        setCurrentlyHeldPositions,
        PosGridlayouts,
        setPosGridlayouts,
        PosGridTabID
    } = useSharingContext();

    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const { announcements, newAnnouncements, isUnmounting, setIsUnmounting } = useAnnouncementContext();
    const {
        GUIInstrument,
        setGUIInstruments,
        setLayouts,
        tabID,
        layouts,
        index
    } = useInstrumentRowContext();

    const latestTick = tickStorage.latest[GUIInstrument.instrumentToken] || null;

    const handleAddPositionClick = () => {
        handleAddPosition(
            GUIInstrument,
            setLayouts,
            tabID,
            latestTick,
            setCurrentlyHeldPositions,
            PosGridlayouts,
            setPosGridlayouts,
            PosGridTabID
        );
    };

    const [popupState, setPopupState] = useState({
        open: false,
        side: 'BUY'
    });

    const [alertPopupOpen, setAlertPopupOpen] = useState(false); // NEW

    const openBuyPopup = (e) => {
        e.stopPropagation();
        setPopupState({ open: true, side: 'BUY' });
    };

    const openSellPopup = (e) => {
        e.stopPropagation();
        setPopupState({ open: true, side: 'SELL' });
    };

    const closePopup = () =>
        setPopupState(prev => ({
            ...prev,
            open: false
        }));

    const handleClick = (event) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);  // use actual button DOM element
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    // This is what the popup calls to actually place the order.
    const handlePlaceOrder = async (orderPayload) => {
        const payloadWithTag = {
            ...orderPayload,
            tag: "Regular"
        };
        const res = await placeKiteOrder(payloadWithTag);
        return res; // { success, message, orderId? }
    };

    return (
        <div className={`actions ${anchorEl ? 'show-actions' : ''}`}>
            <Button
                variant="contained"
                color="primary"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={openBuyPopup}
            >
                B
            </Button>
            <Button
                variant="contained"
                color="secondary"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={openSellPopup}
            >
                S
            </Button>
            <Button
                variant="contained"
                sx={{ backgroundColor: '#4caf50', color: 'white' }}
                onMouseDown={(event) => event.stopPropagation()}
                onClick={handleAddPositionClick}
                disabled={currentlyHeldPositions.some(
                    position => position.instrumentToken === GUIInstrument.instrumentToken
                )}
            >
                P
            </Button>
            <Button
                variant="contained"
                color="default"
                className="small-button"
                onMouseDown={(event) => event.stopPropagation()}
                onClick={() => handleShowDetails(GUIInstrument, setGUIInstruments, setLayouts, tabID, index)}
            >
                <DensitySmallIcon sx={{ fontSize: 16 }} />
            </Button>
            <Button
                variant="contained"
                color="default"
                onMouseDown={(event) => event.stopPropagation()}
                onClick={() => handleDeleteFromOldGrid(GUIInstrument, setGUIInstruments, setLayouts, tabID)}
            >
                <DeleteIcon sx={{ color: '#ff4d4d' }} />
            </Button>
            <Button
                variant="contained"
                color="default"
                className="small-button"
                onMouseDown={(event) => event.stopPropagation()}
                onClick={handleClick}
            >
                <MoreHorizIcon sx={{ fontSize: 22 }} />
            </Button>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                keepMounted
            >
                <MenuItem onClick={(e) => {
                    e.currentTarget.blur();
                    onShowChart(GUIInstrument.instrumentToken, GUIInstrument.tradingsymbol);
                    handleClose();
                }}>
                    Chart
                </MenuItem>
                <MenuItem onClick={(e) => {
                    e.currentTarget.blur();
                    handleShowAnnouncementsMW(
                        GUIInstrument,
                        setGUIInstruments,
                        setLayouts,
                        tabID,
                        layouts,
                        index,
                        announcements,
                        newAnnouncements,
                        setIsUnmounting
                    );
                    handleClose();
                }}>
                    Announcements
                </MenuItem>

                {/* NEW: Alert menu item */}
                <MenuItem onClick={(e) => {
                    e.currentTarget.blur();
                    setAlertPopupOpen(true);
                    handleClose();
                }}>
                    PriceVolume Alert
                </MenuItem>
            </Menu>

            {/* Buy/Sell popup */}
            {popupState.open && (
                <BuySellPopup
                    open={popupState.open}
                    side={popupState.side}
                    onClose={closePopup}
                    instrument={GUIInstrument}
                    tick={latestTick}
                    onSubmit={handlePlaceOrder}
                />
            )}

            {/* NEW: Price/Volume Alert popup */}
            {alertPopupOpen && (
                <PriceVolumeAlertPopup
                    open={alertPopupOpen}
                    onClose={() => setAlertPopupOpen(false)}
                    instrument={GUIInstrument}
                    tick={latestTick}
                />
            )}
        </div>
    );
};

export default ActionButtons;
